import { ApiError } from '@/api/client/http';

/**
 * The assistant's refusals, in words.
 *
 * ---------------------------------------------------------------------------
 * WHY THE CODE IS BOUND AND NOT THE STATUS
 * ---------------------------------------------------------------------------
 * The assistant answers with its own codes, and two of them share a status:
 * `ai.assistant.configuration_required` and `ai.assistant.provider_failed` are
 * both 4xx/5xx failures of a send, but they are different problems with different
 * answers — one says "no provider is configured", the other says "the provider
 * did not answer". A panel that branched on the status would say the wrong one,
 * and "the provider did not answer" is exactly the sentence that sends an
 * operator looking for a problem that does not exist.
 *
 * A code that is not bound here falls through to the SERVER'S OWN message, which
 * is the only reason the console is entitled to state.
 */
const ASSISTANT_FAILURE_WORDING: Record<string, string> = {
  'ai.assistant.not_found': 'The assistant could not find what it was asked about.',
  'ai.assistant.thread_not_found': 'That conversation no longer exists.',
  'ai.assistant.configuration_required': 'No AI provider is configured for this deployment yet.',
  'ai.assistant.provider_failed': 'The AI provider did not answer. Try again in a moment.',
  'ai.assistant.draft_unavailable': 'The assistant could not produce a draft for this page.',
  'ai.assistant.internal_error': 'The assistant request failed.',
};

/** The sentence the panel shows for a failure. */
export function describeAssistantFailure(cause: unknown): string {
  if (cause instanceof ApiError) {
    const wording = ASSISTANT_FAILURE_WORDING[cause.code];
    return wording ?? cause.message;
  }
  if (cause instanceof Error && cause.message !== '') return cause.message;
  return 'The assistant request failed.';
}

/**
 * The sentence for a failure that may not have happened.
 *
 * A query that has not failed carries no error, and "no error" is not a sentence
 * a panel can show. This keeps that distinction in one place rather than in a
 * comparison repeated at every call site.
 */
export function describeOptionalFailure(cause: unknown): string | null {
  if (cause === null || cause === undefined) return null;
  return describeAssistantFailure(cause);
}
